
import { Component, Vue } from 'vue-property-decorator';
import { createPatch, exposeHeaders, handleError, initClient } from '../../../../api';
import {
  JsonPatchDocumentOfTntItem,
  Operation,
  SettingsApiControllerUIClient,
  TntAttributeDefinitionType,
  TntBatch,
  TntBatchUiClient,
  TntItem,
  TntItemAttributeDefinition,
  TntItemAttributeDefinitionUiClient,
  TntItemFilter,
  TntItemPatchRequest,
  TntItemUiClient,
  TntLocation,
  TntLocationUiClient,
  TntOrderType,
  TntProduct,
  TntProductUiClient,
  TntSupplier,
  TntSupplierUiClient,
  TntTransaction,
  TntTransactionUiClient,
} from '../../../backend/backendapi';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import Toasted from 'vue-toasted';
import { Results, VgtParameters, VgtRemoteManger } from '../../../../util/VgtRemoteManger';

Vue.use(Toasted, { duration: 2000 });

@Component({
  name: 'PriceAdjust',
  components: {
    vSelect,
    VueGoodTable,
  },
})
export default class PriceAdjust extends Vue {
  //api
  private readonly batchesClient = initClient(TntBatchUiClient);
  private readonly itemAttributeDefinitionClient = initClient(TntItemAttributeDefinitionUiClient);
  private readonly itemsClient = initClient(TntItemUiClient);
  private readonly locationsClient = initClient(TntLocationUiClient);
  private readonly productsClient = initClient(TntProductUiClient);
  private readonly suppliersClient = initClient(TntSupplierUiClient);
  private readonly transactionsClient = exposeHeaders(initClient(TntTransactionUiClient), 'list');

  private readonly settingsClient = initClient(SettingsApiControllerUIClient);

  //data
  private readonly batches: TntBatch[] = [];
  private readonly items: TntItem[] = [];
  private readonly itemAttributeDefinitions: TntItemAttributeDefinition[] = [];
  private readonly locations: TntLocation[] = [];
  private readonly products: TntProduct[] = [];
  private readonly suppliers: TntSupplier[] = [];

  private settings: { filter?: { locationNames?: string[] } } = {};

  //runtime
  selectedBatchIds: string[] = [];
  selectedProductIds: string[] = [];
  selectedSupplierIds: string[] = [];
  selectedItemIds: string[] = [];
  price: number = 0;
  vgtManager = new VgtRemoteManger<TntTransaction>(this.loadTransactions.bind(this), 'en', [
    { field: 'sourceProductIdentifier', type: 'string' },
    { field: 'sourceProductName', type: 'string' },
    { field: 'sourceSupplierName', type: 'string' },
    { field: 'sourceBatchIdentifier', type: 'string' },
  ]);

  get batchesForDropDown(): TntBatch [] {
    return this.batches.sort((l, r) => l.identifier.localeCompare(r.identifier));
  }

  get canSetPrice(): boolean {
    return this.selectedItemIds.length > 0
        && this.price > 0;
  }

  get columns(): unknown[] {
    return [{
      label: this.$t('tnt.product'),
      field: 'sourceProductName',
    }, {
      label: this.$t('tnt.supplier'),
      field: 'sourceSupplierName',
    }, {
      label: this.$t('tnt.batch'),
      field: 'sourceBatchIdentifier',
    }, {
      label: this.$t('tnt.quantity'),
      field: 'quantity',
      type: 'number',
    }, {
      label: this.$t('tnt.price.price'),
      field: 'sourceItemNetPrice',
      type: 'number',
    },
      ...this.itemAttributeDefinitions.map(e => {
        let type: string;
        switch (e.type) {
          case TntAttributeDefinitionType.Datetime:
            type = 'date';
            break;
          case TntAttributeDefinitionType.Integer:
          case TntAttributeDefinitionType.Double:
            type = 'number';
            break;
          default:
            type = 'string';
            break;
        }
        return ({
          label: e.name,
          field: 'attr_' + e.id,
          type: type,
          sortable: false
        });
      }),
    ];
  }

  get extraFilter(): string {
    const batchIds = this.selectedBatchIds.length ? this.selectedBatchIds : undefined;
    const productIds = this.selectedProductIds.length ? this.selectedProductIds : undefined;
    const supplierIds = this.selectedSupplierIds.length ? this.selectedSupplierIds : undefined;
    let locationIds = this.settings.filter?.locationNames?.map(e => this.locationsByIdentifier.get(e)?.id).filter(e => !!e).map(e => e!);
    locationIds = locationIds?.length ? locationIds : undefined;

    const extraFilters = [
      'orderType IN (' + TntOrderType.Produce + ', ' + TntOrderType.Consume + ')',
      batchIds ? 'sourceBatchId IN (' + batchIds + ')' : undefined,
      locationIds ? 'sourceLocationId IN (' + locationIds + ')' : undefined,
      productIds ? 'sourceProductId IN (' + productIds + ')' : undefined,
      supplierIds ? 'sourceSupplierId IN (' + supplierIds + ')' : undefined,
    ];

    return extraFilters.filter(e => !!e).join(' AND ');
  }

  get paginationOptions(): unknown {
    return {
      enabled: true,
      perPage: 20,
    };
  }

  get productsForDropDown(): { id: string, name: string } [] {
    return this.products.map(e => ({ id: e.id, name: e.identifier + ' - ' + e.name })).sort((l, r) => l.name.localeCompare(r.name));
  }

  get quantitySum(): number {
    return this.itemsForTable.map(e => e.quantity).reduce((sum, q) => sum + q, 0);
  }

  get searchOptions(): unknown {
    return { enabled: true, placeholder: this.$t('tnt.search') };
  };

  get selectOptions(): unknown {
    return {
      enabled: true,
      selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
      selectionInfoClass: 'custom-class',
      selectionText: this.$t('tnt.selected'),
      clearSelectionText: this.$t('tnt.clear'),
      disableSelectInfo: false, // disable the select info panel on top
      selectAllByGroup: true, // when used in combination with a grouped table, add a checkbox in the header row to check/uncheck the entire group
    };
  }

  get supplierForDropDown(): TntSupplier[] {
    return this.suppliers.sort((l, r) => l.name.localeCompare(r.name));
  }

  get itemsForTable(): {
    id: string,
    sourceBatchIdentifier?: string,
    sourceProductName?: string,
    sourceSupplierName?: string,
    quantity: number,
    sourceItemNetPrice?: number
  }[] {
    return this.vgtManager.results.data
        .map(transaction => {
          const item = this.itemsById.get(transaction.sourceItemId);
          if (!item) {//should never occur
            return undefined;
          }

          const attrs = {};
          this.itemAttributeDefinitions.forEach(def => {
            attrs['attr_' + def.id] = item.attributes?.find(attr => attr.definitionId == def.id)?.value;
          });
          return ({
            id: item.id,
            sourceBatchIdentifier: this.batchesById.get(item.batchId)?.identifier,
            sourceProductName: this.productsById.get(item.productId)
                ? this.productsById.get(item.productId)!.identifier + ' - ' + this.productsById.get(item.productId)!.name
                : undefined,
            sourceSupplierName: item.supplierId && this.suppliersById.get(item.supplierId)?.name,
            quantity: transaction.quantity,
            sourceItemNetPrice: item.netPrice,
            ...attrs,
          });
        })
        .filter(e => !!e)
        .map(e => e!);
  }

  private get batchesById(): Map<string, TntBatch> {
    return new Map(this.batches.map(e => [e.id, e]));
  }

  private get itemsById(): Map<string, TntItem> {
    return new Map(this.items.map(e => [e.id, e]));
  }

  private get locationsByIdentifier(): Map<string, TntLocation> {
    return new Map(this.locations.map(e => [e.identifier, e]));
  }

  private get productsById(): Map<string, TntProduct> {
    return new Map(this.products.map(e => [e.id, e]));
  }

  private get suppliersById(): Map<string, TntSupplier> {
    return new Map(this.suppliers.map(e => [e.id, e]));
  }

  async mounted(): Promise<void> {
    await this.reloadAll();
    this.vgtManager.initExtraFilter(this.extraFilter);
  }

  private reloadAll(): Promise<void> {
    return Promise.all([
      this.reloadBatches(),
      this.reloadItemAttributes(),
      this.reloadLocations(),
      this.reloadProducts(),
      this.reloadSuppliers(),
      this.reloadSettings(),
    ]).then(_ => undefined);
  }

  private async reloadBatches(): Promise<void> {
    const batches = (await this.batchesClient.getAll()).result!;
    this.batches.splice(0);
    this.batches.push(...batches);
  }

  private async reloadItemAttributes(): Promise<void> {
    const itemAttributeDefinitions = (await this.itemAttributeDefinitionClient.getAll()).result!;
    this.itemAttributeDefinitions.splice(0);
    this.itemAttributeDefinitions.push(...itemAttributeDefinitions);
  }

  private async reloadLocations(): Promise<void> {
    const locations = (await this.locationsClient.getAll()).result!;
    this.locations.splice(0);
    this.locations.push(...locations);
  }

  private async reloadProducts(): Promise<void> {
    const products = (await this.productsClient.getAll()).result!;
    this.products.splice(0);
    this.products.push(...products);
  }

  private async reloadSuppliers(): Promise<void> {
    const suppliers = (await this.suppliersClient.getAll()).result!;
    this.suppliers.splice(0);
    this.suppliers.push(...suppliers);
  }

  private async reloadSettings(): Promise<void> {
    this.settings = (await this.settingsClient.getSystemSetting('tnt.priceadjust')).result!;
  }

  //event handlers
  onVgtSelectionChanged(event: { selectedRows: { id: string }[] }): void {
    this.selectedItemIds.splice(0);
    this.selectedItemIds.push(...event.selectedRows.map(e => e.id));
  }

  private async loadTransactions(params: VgtParameters): Promise<Results<TntTransaction>> {
    const response = await this.transactionsClient.list(params.filter, params.sort, params.page, params.pageSize);
    const ret = { data: response.content, totalRows: parseInt(response.headers.get('total') ?? '0') };

    // load related items
    const itemIds = Array.from(new Set(response.content.map(e => e.sourceItemId)));
    const itemFilter = new TntItemFilter({ ids: itemIds });
    const items = (await this.itemsClient.getByFilter(itemFilter)).result!;
    this.items.splice(0);
    this.items.push(...items);

    return ret;
  }

  async onFilter(): Promise<void> {
    this.vgtManager.extraFilter = this.extraFilter;
  }

  async onSetPrice(): Promise<void> {
    console.log(this.selectedItemIds);
    if (this.selectedItemIds.length == 0 || this.price <= 0) {
      return;
    }

    try {
      const req = new TntItemPatchRequest({
        ids: this.selectedItemIds,
        patch: createPatch<JsonPatchDocumentOfTntItem>(new Operation({ op: 'replace', path: '/netPrice', value: this.price })),
      });

      await this.itemsClient.patch(req);
      this.$toasted.info(this.$t('tnt.price.doneSet').toString());
      this.selectedItemIds.splice(0);
      this.price = 0;
    } catch (error) {
      handleError(this.$toasted, this.$i18n, error);
    } finally {
      await this.onFilter();
    }
  }
}
